import React from 'react';
import {Map} from 'immutable';
import {callAction, getStoreByName} from 'client/framework';
import {history} from 'client/history';
import appStore from 'client/AppStore';
import masqueradeActions from 'client/generic/Masquerade/Masquerade.actions';
import SettingsTable from 'sg/Tables/SettingsTable/SettingsTable.react';
import DownloadCSV from 'sg/Tables/SettingsTable/DownloadCSV.react';
import {Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import {sortDirections} from 'sg/Tables/tableConstants';
import Button from 'sg/Button/Button.react';
import ClassroomStatusFilters from '../Filters/ClassroomStatusFilters.react';
import ExpandSubjectsFilter from '../Filters/ExpandSubjectsFilter.react';
import EmptyUsageReport from './EmptyUsageReport.react';
import usageReportsStore from '../UsageReports.store';
import {
  getCountOfSchoolClassroomsContent,
  getCountOfSchoolStudentsContent,
  getCountOfSchoolAssignmentsContent,
  getSchoolSubjectsContent,
  getStudentAttemptsContent,
  getPerformanceBarContent,
  getCSVHeaders
} from './utils';

const usageReportStoreName = 'TeacherUsageReportTableStore';

export default class TeachersReport extends React.Component {
  makeContentBuilder(func) {
    return (teacher) => func(teacher, usageReportsStore.areSubjectsExpanded());
  }

  buildClassesContent = this.makeContentBuilder(getCountOfSchoolClassroomsContent);

  buildSubjectsContent = this.makeContentBuilder(getSchoolSubjectsContent);

  buildStudentsContent = this.makeContentBuilder(getCountOfSchoolStudentsContent);

  buildAttemptsContent = this.makeContentBuilder(getStudentAttemptsContent);

  buildAssignmentsContent = this.makeContentBuilder(getCountOfSchoolAssignmentsContent);

  buildPerformanceContent = this.makeContentBuilder(getPerformanceBarContent);

  buildTeacherContent(teacher) {
    return (
      <div className='usage-report__user-cell'>
        <span className='usage-report__user-name'>{teacher.getFullName({formal: true})}</span>
        <span className='usage-report__user-email'>{teacher.getEmail()}</span>
      </div>
    );
  }

  buildViewClassesContent = (teacher) => {
    return (
      <Button
        className='usage-report__table-button'
        onClick={() => this.masqeradeAsTeacher(teacher)}
        outline
        text='View classes'
      />
    );
  };

  masqeradeAsTeacher(teacher) {
    const redirect = appStore.routerProps.location.pathname;
    callAction(masqueradeActions.ENABLE_MASQUERADING, {
      user: teacher,
      school: Map({
        id: appStore.routerProps.params.schoolId
      }),
      origin: redirect
    });
    history.pushState(null, '/classes');
  }

  render() {
    const areSubjectsExpanded = usageReportsStore.areSubjectsExpanded();
    const mutator = (data) => {
      const searchTerm = getStoreByName(usageReportStoreName).getSearchTerm();
      return usageReportsStore.getTeachersExpandedSubjectsMutator(searchTerm)(data);
    };

    return (
      <SettingsTable
        title='Usage'
        topFilters={<ClassroomStatusFilters />}
        bottomFilters={<ExpandSubjectsFilter />}
        defaultSortDirection={sortDirections.DESCENDING}
        query={usageReportsStore.getTeachersQuery()}
        dataMutator={areSubjectsExpanded ? mutator : null}
        searchFilters={usageReportsStore.getTeacherSearchFilters}
        storeName={usageReportStoreName}
        optionsMenu={
          <DownloadCSV
            csvQuery={usageReportsStore.getTeachersCSVQuery}
            tableStoreName={usageReportStoreName}
            fileName='teachers-usage-report'
            getCSVHeaders={getCSVHeaders('teachers')}
          />
        }
        className='usage-report__table'
        emptyTableElement={<EmptyUsageReport headerText={`You don't have any teachers!`} />}
      >
        <Column
          heading='Teacher'
          sortBy='last_name'
          alwaysVisible
          content={this.buildTeacherContent}
        />
        <Column
          heading='Classes'
          sortBy={!areSubjectsExpanded ? 'meta.count_of_school_classrooms' : null}
          className='sg-table__td--align-right'
          defaultVisible
          content={this.buildClassesContent}
        />
        <Column
          heading='Subjects'
          sortBy={!areSubjectsExpanded ? 'meta.school_subject_names' : null}
          defaultVisible
          content={this.buildSubjectsContent}
        />
        <Column
          heading='Students'
          sortBy={!areSubjectsExpanded ? 'meta.count_of_school_students' : null}
          className='sg-table__td--align-right'
          defaultVisible
          content={this.buildStudentsContent}
        />
        <Column
          defaultSortParam
          heading='Student Attempts'
          sortBy={!areSubjectsExpanded ? 'meta.count_of_school_guesses' : null}
          defaultVisible
          className='sg-table__td--align-right'
          content={this.buildAttemptsContent}
        />
        <Column
          heading='Assignments'
          sortBy={!areSubjectsExpanded ? 'meta.count_of_school_assignments' : null}
          className='sg-table__td--align-right'
          content={this.buildAssignmentsContent}
        />
        <Column heading='Performance' content={this.buildPerformanceContent} />
        <Column alwaysVisible content={this.buildViewClassesContent} />
      </SettingsTable>
    );
  }
}
